import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Trash2,
  Users,
  Target,
  Info,
  RefreshCw,
  Eye,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { useToast } from "../hooks/use-toast";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  fetchUserGroup as apiFetchUserGroup,
  createUserGroup,
  updateUserGroup,
  refreshGroupMemberCount,
  UserGroup,
} from "../api/user-groups";

// Predefined group names that match database functions
const PREDEFINED_GROUP_NAMES = [
  // Content Consumption
  {
    name: "Active Book Readers",
    category: "Content Consumption",
    description:
      "Users who actively read books with multiple reading sessions (3+ reads)",
    criteria: { book_reads_count: 3 },
  },
  {
    name: "Audio Content Consumers",
    category: "Content Consumption",
    description: "Users who primarily consume audio content (3+ audio plays)",
    criteria: { audio_plays_count: 3 },
  },
  {
    name: "Multi-Format Consumers",
    category: "Content Consumption",
    description: "Users who engage with both text and audio content (2+ each)",
    criteria: { book_reads_count: 2, audio_plays_count: 2 },
  },
  {
    name: "Content Completion Champions",
    category: "Content Consumption",
    description: "Users with high completion rates for books and audio (80%+)",
    criteria: { completion_rate: 80 },
  },

  // Engagement & Interaction
  {
    name: "Engaged Highlighters",
    category: "Engagement & Interaction",
    description:
      "Users who actively highlight and annotate content (3+ highlights)",
    criteria: { highlights_count: 3 },
  },
  {
    name: "Feedback Providers",
    category: "Engagement & Interaction",
    description: "Users who provide ratings and reviews",
    criteria: { feedback_count: 1 },
  },
  {
    name: "Category Enthusiasts",
    category: "Engagement & Interaction",
    description:
      "Users who explore multiple content categories (3+ categories)",
    criteria: { selected_categories_count: 3 },
  },
  {
    name: "Content Discovery Users",
    category: "Engagement & Interaction",
    description:
      "Users who actively browse and discover new content (10+ views)",
    criteria: { book_views_count: 10 },
  },

  // Subscription & Monetization
  {
    name: "Subscription Holders",
    category: "Subscription & Monetization",
    description: "Users with active subscriptions",
    criteria: { subscription_status: "active" },
  },
  {
    name: "Free Content Users",
    category: "Subscription & Monetization",
    description: "Users who primarily consume free content",
    criteria: { subscription_status: "free" },
  },
  {
    name: "Free Content Users with 30+ Minutes Reading",
    category: "Subscription & Monetization",
    description: "Free users who have spent 30+ minutes reading content",
    criteria: { subscription_status: "free", total_reading_minutes: 30 },
  },
  {
    name: "Free Users - No Plan Ever",
    category: "Subscription & Monetization",
    description: "Users who have never had any subscription plan",
    criteria: { subscription_status: "no_plan" },
  },
  {
    name: "Expired Subscribers",
    category: "Subscription & Monetization",
    description: "Users with expired subscriptions",
    criteria: { subscription_status: "expired" },
  },
  {
    name: "Cancelled Subscribers",
    category: "Subscription & Monetization",
    description: "Users with cancelled subscriptions",
    criteria: { subscription_status: "cancelled" },
  },
  {
    name: "Free Users with Account Age 30+ Days",
    category: "Subscription & Monetization",
    description: "Free users with accounts older than 30 days",
    criteria: { subscription_status: "free", account_age_min_days: 30 },
  },
  {
    name: "Free Users with Account Age 90+ Days",
    category: "Subscription & Monetization",
    description: "Free users with accounts older than 90 days",
    criteria: { subscription_status: "free", account_age_min_days: 90 },
  },
  {
    name: "Free Users with 5+ Books Read",
    category: "Subscription & Monetization",
    description: "Free users who have read 5 or more books",
    criteria: { subscription_status: "free", books_read_count: 5 },
  },
  {
    name: "Free Users with 60+ Minutes Reading",
    category: "Subscription & Monetization",
    description: "Free users who have spent 60+ minutes reading",
    criteria: { subscription_status: "free", total_reading_minutes: 60 },
  },
  {
    name: "Free Users with 10+ Books Read",
    category: "Subscription & Monetization",
    description: "Free users who have read 10 or more books",
    criteria: { subscription_status: "free", books_read_count: 10 },
  },
  {
    name: "Free Users with 2+ Hours Reading",
    category: "Subscription & Monetization",
    description: "Free users who have spent 2+ hours reading",
    criteria: { subscription_status: "free", total_reading_minutes: 120 },
  },
  {
    name: "Expired Subscribers with 30+ Days Account Age",
    category: "Subscription & Monetization",
    description: "Expired subscribers with accounts older than 30 days",
    criteria: { subscription_status: "expired", account_age_min_days: 30 },
  },
  {
    name: "Cancelled Subscribers with Reading Activity",
    category: "Subscription & Monetization",
    description: "Cancelled subscribers who have read at least 1 book",
    criteria: { subscription_status: "cancelled", books_read_count: 1 },
  },
  {
    name: "Free Users - High Engagement",
    category: "Subscription & Monetization",
    description:
      "Free users with high engagement (5+ books, 60+ minutes, 30+ days account)",
    criteria: {
      subscription_status: "free",
      books_read_count: 5,
      total_reading_minutes: 60,
      account_age_min_days: 30,
    },
  },
  {
    name: "High-Value Subscribers",
    category: "Subscription & Monetization",
    description: "Users with long-term or premium subscriptions (30+ days)",
    criteria: { subscription_duration_days: 30 },
  },
  {
    name: "At-Risk Churn Users",
    category: "Subscription & Monetization",
    description: "Users showing signs of potential churn (14+ days inactive)",
    criteria: { last_activity_days: 14 },
  },

  // User Lifecycle
  {
    name: "Recent Registrations",
    category: "User Lifecycle",
    description: "Users who registered in the last 30 days",
    criteria: { registration_days: 30 },
  },
  {
    name: "Inactive Users",
    category: "User Lifecycle",
    description:
      "Users who haven't been active for extended periods (60+ days)",
    criteria: { last_activity_days: 60 },
  },
  {
    name: "Engagement Leaders",
    category: "User Lifecycle",
    description: "Users with exceptional engagement across all metrics",
    criteria: { overall_engagement_score: 90 },
  },

  // Demographic & Behavioral
  {
    name: "Age Group Specific",
    category: "Demographic & Behavioral",
    description: "Users grouped by age demographics for targeted content",
    criteria: { age_group: "specific" },
  },
  {
    name: "Referral Users",
    category: "Demographic & Behavioral",
    description: "Users who came through referral programs",
    criteria: { referred_by: "not_null" },
  },
  {
    name: "Platform Specific Users",
    category: "Demographic & Behavioral",
    description: "Users who primarily use specific platforms",
    criteria: { platform_preference: "specific" },
  },

  // Feature Adoption
  {
    name: "Feature Adopters",
    category: "Feature Adoption",
    description: "Users who actively use advanced features",
    criteria: { highlights_used: true, feedback_provided: true },
  },
  {
    name: "High Completion Rate Users",
    category: "Feature Adoption",
    description: "Users who complete most content they start (90%+)",
    criteria: { completion_rate: 90 },
  },
];

// Enhanced criteria fields for targeting users (for custom groups)
const CRITERIA_FIELDS = [
  {
    key: "subscription_status",
    label: "Subscription Status",
    type: "select",
    options: ["active", "free", "expired", "cancelled", "no_plan", "trial"],
    description: "Filter by user subscription status",
  },
  {
    key: "subscription_tier",
    label: "Subscription Tier",
    type: "select",
    options: ["basic", "premium", "enterprise"],
    description: "Filter by subscription plan tier",
  },
  {
    key: "registration_date_after",
    label: "Registered After",
    type: "date",
    description: "Users who registered after this date",
  },
  {
    key: "last_activity_days",
    label: "Last Activity (days ago)",
    type: "number",
    description: "Users inactive for this many days",
  },
  {
    key: "books_read_count",
    label: "Books Read Count",
    type: "number",
    description: "Minimum number of books read",
  },
  {
    key: "reading_frequency",
    label: "Reading Frequency",
    type: "select",
    options: ["daily", "weekly", "monthly", "occasional"],
    description: "User reading activity frequency",
  },
  {
    key: "content_preference",
    label: "Content Preference",
    type: "select",
    options: ["audio", "text", "both"],
    description: "Preferred content format",
  },
  {
    key: "platform_preference",
    label: "Platform Preference",
    type: "select",
    options: ["mobile", "web", "tablet", "mixed"],
    description: "Preferred platform for reading",
  },
  {
    key: "total_reading_minutes",
    label: "Total Reading Time (minutes)",
    type: "number",
    description: "Minimum total reading time in minutes",
  },
  {
    key: "account_age_min_days",
    label: "Account Age (minimum days)",
    type: "number",
    description: "Users with accounts at least this many days old",
  },
  {
    key: "account_age_max_days",
    label: "Account Age (maximum days)",
    type: "number",
    description: "Users with accounts no older than this many days",
  },
  {
    key: "account_age_days",
    label: "Account Age (exact days)",
    type: "number",
    description: "Users with accounts exactly this many days old",
  },
];

export default function UserGroupForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  const [formData, setFormData] = useState<Partial<UserGroup>>({
    name: "",
    description: "",
    criteria: {},
    is_active: true,
  });

  const [loading, setLoading] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMembers, setPreviewMembers] = useState<unknown[]>([]);
  const [groupType, setGroupType] = useState<"predefined" | "custom">(
    "predefined"
  );
  const [selectedPredefinedGroup, setSelectedPredefinedGroup] =
    useState<string>("");

  const isEditing = !!id;

  const fetchUserGroup = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetchUserGroup(parseInt(id!));
      if (data) {
        setFormData(data);
      }
    } catch (error) {
      console.error("Error fetching user group:", error);
      toast({
        title: "Error",
        description: "Failed to fetch user group details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    if (isEditing) {
      fetchUserGroup();
    }
  }, [isEditing, fetchUserGroup]);

  const handlePreviewCount = async () => {
    if (!formData.criteria || Object.keys(formData.criteria).length === 0) {
      setPreviewCount(0);
      return;
    }

    try {
      setPreviewLoading(true);

      // Use the real function to get actual count
      const { countUsersByCriteria } = await import(
        "../services/userGroupService"
      );
      const actualCount = await countUsersByCriteria(
        formData.criteria as Record<string, string | number>
      );
      setPreviewCount(actualCount);

      toast({
        title: "Preview Updated",
        description: `${actualCount} users match these criteria.`,
      });
    } catch (error) {
      console.error("Error previewing count:", error);
      setPreviewCount(0);
      toast({
        title: "Error",
        description: "Failed to preview member count.",
        variant: "destructive",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      toast({
        title: "Error",
        description: "Group name is required.",
        variant: "destructive",
      });
      return;
    }

    if (groupType === "predefined" && !selectedPredefinedGroup) {
      toast({
        title: "Error",
        description: "Please select a predefined group.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        description: formData.description || null,
        criteria: formData.criteria || {},
        is_active: formData.is_active ?? true,
      };

      let createdGroup: { id: number; name: string; criteria: any } | null =
        null;

      if (isEditing && formData.id) {
        await updateUserGroup(formData.id, payload);
        createdGroup = {
          id: formData.id,
          name: formData.name,
          criteria: formData.criteria,
        };
      } else {
        createdGroup = await createUserGroup(payload);
      }

      // Refresh member count immediately after creating/updating
      try {
        const { refreshGroupMemberCount } = await import(
          "../services/userGroupService"
        );
        const refreshResult = await refreshGroupMemberCount(
          createdGroup.id,
          createdGroup.name,
          createdGroup.criteria as any
        );

        if (refreshResult.success) {
          toast({
            title: "Success",
            description: `User group ${
              isEditing ? "updated" : "created"
            } successfully with ${refreshResult.memberCount} members.`,
          });
        } else {
          toast({
            title: "Success",
            description: `User group ${
              isEditing ? "updated" : "created"
            } successfully, but member count calculation failed: ${
              refreshResult.error
            }`,
            variant: "destructive",
          });
        }
      } catch (refreshError) {
        console.error("Error refreshing member count:", refreshError);
        toast({
          title: "Success",
          description: `User group ${
            isEditing ? "updated" : "created"
          } successfully, but member count will be calculated later.`,
        });
      }

      navigate("/admin/user-groups");
    } catch (error) {
      console.error("Error saving user group:", error);
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? "update" : "create"} user group.`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateCriteria = (key: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      criteria: {
        ...((prev.criteria as Record<string, string | number>) || {}),
        [key]: value,
      },
    }));
  };

  const removeCriteria = (key: string) => {
    const newCriteria = {
      ...((formData.criteria as Record<string, string | number>) || {}),
    };
    delete newCriteria[key];
    setFormData((prev) => ({
      ...prev,
      criteria: newCriteria,
    }));
  };

  const getCriteriaField = (field: string) => {
    return CRITERIA_FIELDS.find((f) => f.key === field);
  };

  const handlePredefinedGroupSelection = async (groupName: string) => {
    const predefinedGroup = PREDEFINED_GROUP_NAMES.find(
      (g) => g.name === groupName
    );
    if (predefinedGroup) {
      setFormData((prev) => ({
        ...prev,
        name: predefinedGroup.name,
        description: predefinedGroup.description,
        criteria: predefinedGroup.criteria,
      }));
      setSelectedPredefinedGroup(groupName);

      // Automatically calculate member count for predefined groups
      try {
        setPreviewLoading(true);
        const { countUsersForPredefinedGroup } = await import(
          "../services/userGroupService"
        );
        const actualCount = await countUsersForPredefinedGroup(groupName);
        setPreviewCount(actualCount);
      } catch (error) {
        console.error("Error calculating predefined group count:", error);
        setPreviewCount(0);
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  const getCategoryColor = (category: string): string => {
    const colors = {
      "Content Consumption":
        "text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-300",
      "Engagement & Interaction":
        "text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-300",
      "Subscription & Monetization":
        "text-purple-600 bg-purple-50 dark:bg-purple-950 dark:text-purple-300",
      "User Lifecycle":
        "text-orange-600 bg-orange-50 dark:bg-orange-950 dark:text-orange-300",
      "Demographic & Behavioral":
        "text-indigo-600 bg-indigo-50 dark:bg-indigo-950 dark:text-indigo-300",
      "Feature Adoption":
        "text-pink-600 bg-pink-50 dark:bg-pink-950 dark:text-pink-300",
    };
    return (
      colors[category] ||
      "text-gray-600 bg-gray-50 dark:bg-gray-950 dark:text-gray-300"
    );
  };

  if (loading && isEditing) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <AdminLayout
      title={isEditing ? "Edit User Group" : "Create User Group"}
      mainClassName="flex-1 overflow-y-auto p-6 bg-background max-w-5xl mx-auto"
    >
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/admin/user-groups")}
          className="flex items-center gap-2 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to User Groups
        </Button>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Group Type Selection */}
          <Card className="border-2 border-dashed border-gray-200 dark:border-gray-700">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Choose Group Type</CardTitle>
              <CardDescription>
                Select how you want to create your user group
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    groupType === "predefined"
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
                  onClick={() => setGroupType("predefined")}
                >
                  <div className="flex items-start space-x-3">
                    <div
                      className={`p-2 rounded-lg ${
                        groupType === "predefined"
                          ? "bg-blue-100 dark:bg-blue-900"
                          : "bg-gray-100 dark:bg-gray-800"
                      }`}
                    >
                      <Target className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                        Predefined Groups
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Choose from optimized groups with automatic member
                        counting
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    groupType === "custom"
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
                  onClick={() => setGroupType("custom")}
                >
                  <div className="flex items-start space-x-3">
                    <div
                      className={`p-2 rounded-lg ${
                        groupType === "custom"
                          ? "bg-blue-100 dark:bg-blue-900"
                          : "bg-gray-100 dark:bg-gray-800"
                      }`}
                    >
                      <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                        Custom Group
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Create your own group with custom targeting criteria
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {groupType === "predefined" && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <div className="p-1 bg-green-100 dark:bg-green-900 rounded">
                      <Info className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="text-sm text-green-800 dark:text-green-200">
                      <strong>Recommended:</strong> Predefined groups are
                      optimized and will automatically calculate member counts
                      correctly.
                    </p>
                  </div>
                </div>
              )}

              {groupType === "custom" && (
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <div className="p-1 bg-amber-100 dark:bg-amber-900 rounded">
                      <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      <strong>Note:</strong> Custom groups allow flexible
                      criteria, but member counting may require manual refresh.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Predefined Group Selection */}
          {groupType === "predefined" && (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">
                  Select Predefined Group
                </CardTitle>
                <CardDescription>
                  Choose from our optimized user groups that work with database
                  functions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Group Selection</Label>
                  <Select
                    value={selectedPredefinedGroup}
                    onValueChange={handlePredefinedGroupSelection}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Choose a predefined group..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {Object.entries(
                        PREDEFINED_GROUP_NAMES.reduce((acc, group) => {
                          if (!acc[group.category]) acc[group.category] = [];
                          acc[group.category].push(group);
                          return acc;
                        }, {} as Record<string, typeof PREDEFINED_GROUP_NAMES>)
                      ).map(([category, groups]) => (
                        <div key={category}>
                          <div className="px-3 py-2 text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 sticky top-0">
                            {category}
                          </div>
                          {groups.map((group) => (
                            <SelectItem
                              key={group.name}
                              value={group.name}
                              className="py-3"
                            >
                              <div className="flex flex-col items-start text-left space-y-1">
                                <span className="font-medium text-sm">
                                  {group.name}
                                </span>
                                <span className="text-xs text-gray-500 text-left leading-relaxed">
                                  {group.description}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedPredefinedGroup && (
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge
                        variant="outline"
                        className={`${getCategoryColor(
                          PREDEFINED_GROUP_NAMES.find(
                            (g) => g.name === selectedPredefinedGroup
                          )?.category || ""
                        )} border-current`}
                      >
                        {
                          PREDEFINED_GROUP_NAMES.find(
                            (g) => g.name === selectedPredefinedGroup
                          )?.category
                        }
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        ✓ Database Compatible
                      </Badge>
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {selectedPredefinedGroup}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {
                        PREDEFINED_GROUP_NAMES.find(
                          (g) => g.name === selectedPredefinedGroup
                        )?.description
                      }
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Basic Information */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Basic Information</CardTitle>
              <CardDescription>
                Set up the basic details for your user group
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">
                    Group Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder={
                      groupType === "predefined"
                        ? "Select a predefined group above"
                        : "e.g., Premium Subscribers, Active Readers"
                    }
                    required
                    disabled={groupType === "predefined"}
                    className="h-11"
                  />
                  {groupType === "predefined" && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      Group name is automatically set when you select a
                      predefined group
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="is_active" className="text-sm font-medium">
                    Status
                  </Label>
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50 dark:bg-gray-900">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Active</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Can be used for campaigns
                      </p>
                    </div>
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, is_active: checked }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={formData.description || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Describe the purpose of this user group for marketing campaigns..."
                  rows={3}
                  className="resize-none"
                />
                <p className="text-xs text-gray-500">
                  Optional: Help your team understand the purpose of this group
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Targeting Criteria - Only for Custom Groups */}
          {groupType === "custom" && (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Targeting Criteria</CardTitle>
                <CardDescription>
                  Define criteria to target specific users for your campaigns
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="p-1 bg-blue-100 dark:bg-blue-900 rounded">
                      <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        How Targeting Works
                      </p>
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        Add criteria to target specific users. Users matching{" "}
                        <strong>ALL</strong> criteria will be included in this
                        group. Leave empty to include all active users.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Available Criteria
                    </h3>
                    <p className="text-xs text-gray-500">
                      {Object.keys(formData.criteria || {}).length} criteria
                      selected
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {CRITERIA_FIELDS.map((field) => {
                      const hasValue =
                        formData.criteria &&
                        formData.criteria[field.key] !== undefined;

                      return (
                        <div
                          key={field.key}
                          className={`p-4 rounded-lg border transition-all ${
                            hasValue
                              ? "border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800"
                              : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <Label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {field.label}
                              </Label>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                {field.description}
                              </p>
                            </div>
                            {hasValue && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeCriteria(field.key)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>

                          {field.type === "select" && field.options ? (
                            <Select
                              value={
                                formData.criteria?.[field.key] || undefined
                              }
                              onValueChange={(value) => {
                                if (value === "none") {
                                  removeCriteria(field.key);
                                } else {
                                  updateCriteria(field.key, value);
                                }
                              }}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue
                                  placeholder={`Select ${field.label.toLowerCase()}`}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">
                                  Not specified
                                </SelectItem>
                                {field.options.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option.charAt(0).toUpperCase() +
                                      option.slice(1)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : field.type === "date" ? (
                            <Input
                              type="date"
                              value={formData.criteria?.[field.key] || ""}
                              onChange={(e) =>
                                updateCriteria(field.key, e.target.value)
                              }
                              className="h-10"
                            />
                          ) : field.type === "number" ? (
                            <Input
                              type="number"
                              min="0"
                              value={formData.criteria?.[field.key] || ""}
                              onChange={(e) =>
                                updateCriteria(
                                  field.key,
                                  parseInt(e.target.value) || 0
                                )
                              }
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                              className="h-10"
                            />
                          ) : (
                            <Input
                              value={formData.criteria?.[field.key] || ""}
                              onChange={(e) =>
                                updateCriteria(field.key, e.target.value)
                              }
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                              className="h-10"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Current Criteria Summary */}
                {formData.criteria &&
                  Object.keys(formData.criteria).length > 0 && (
                    <div className="pt-4 border-t">
                      <Label className="text-sm font-medium">
                        Applied Criteria:
                      </Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Object.entries(formData.criteria).map(
                          ([key, value]) => {
                            const field = getCriteriaField(key);
                            if (!value) return null;

                            return (
                              <Badge
                                key={key}
                                variant="secondary"
                                className="flex items-center gap-1"
                              >
                                {field?.label}: {String(value)}
                                <button
                                  type="button"
                                  onClick={() => removeCriteria(key)}
                                  className="ml-1 hover:text-red-600"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </Badge>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}

                {/* Preview */}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Target Audience Preview
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        See how many users match your criteria
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handlePreviewCount}
                      disabled={previewLoading}
                      className="h-9"
                    >
                      {previewLoading ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="h-4 w-4 mr-2" />
                      )}
                      {previewLoading ? "Calculating..." : "Preview Count"}
                    </Button>
                  </div>
                  {previewCount !== null && (
                    <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-900 dark:text-green-100">
                          Approximately {previewCount} users match your criteria
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Preview for Predefined Groups */}
          {groupType === "predefined" && selectedPredefinedGroup && (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Group Preview</CardTitle>
                <CardDescription>
                  Preview the selected predefined group and its criteria
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge
                      variant="outline"
                      className={`${getCategoryColor(
                        PREDEFINED_GROUP_NAMES.find(
                          (g) => g.name === selectedPredefinedGroup
                        )?.category || ""
                      )} border-current`}
                    >
                      {
                        PREDEFINED_GROUP_NAMES.find(
                          (g) => g.name === selectedPredefinedGroup
                        )?.category
                      }
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      ✓ Database Compatible
                    </Badge>
                  </div>

                  <h4 className="font-semibold text-lg text-gray-900 dark:text-gray-100 mb-3">
                    {selectedPredefinedGroup}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                    {
                      PREDEFINED_GROUP_NAMES.find(
                        (g) => g.name === selectedPredefinedGroup
                      )?.description
                    }
                  </p>

                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Targeting Criteria:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(
                        PREDEFINED_GROUP_NAMES.find(
                          (g) => g.name === selectedPredefinedGroup
                        )?.criteria || {}
                      ).map(([key, value]) => (
                        <Badge
                          key={key}
                          variant="outline"
                          className="text-xs px-2 py-1"
                        >
                          {key}: {String(value)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Member Count Preview
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        See how many users are in this group
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handlePreviewCount}
                      disabled={previewLoading}
                      className="h-9"
                    >
                      {previewLoading ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="h-4 w-4 mr-2" />
                      )}
                      {previewLoading ? "Calculating..." : "Preview Count"}
                    </Button>
                  </div>
                  {previewCount !== null && (
                    <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-900 dark:text-green-100">
                          Approximately {previewCount} users in this group
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="sticky bottom-0 bg-background border-t pt-6 mt-8">
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/admin/user-groups")}
                className="h-11 px-6"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="h-11 px-8">
                <Save className="h-4 w-4 mr-2" />
                {loading
                  ? "Saving..."
                  : isEditing
                  ? "Update Group"
                  : "Create Group"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
